import { Label, LineItem, Value } from "../common";
import { QuantityLine, TotalLine } from "../LineItems";
import React, { useContext } from "react";
import { AppContext } from "../../../../../AppDataProvider/AppDataProvider";
import { BookingPrice } from "../getBookingPrices/getBookingPrices";
import { PaymentOption } from "../../../../../GeneratedGraphQL";
import { useTranslation } from "react-i18next";
type UnvalidatedSummaryProps = {
  bookingPrices: BookingPrice[];
  getCurrencyDisplay: (value: number) => string;
};

/**
 * Returns a price summary for the booking which has not been validated on the
 * back end.
 *
 * If the user has progressed through the booking flow far enough that we can
 * validate the booking price on the back end (ie, they have a reservation),
 * a validated summary will render instead. This summary is just a way of
 * keeping track of the current price through the booking flow, and does not
 * directly determine the price the user will pay.
 */
export function UnvalidatedSummary({
  bookingPrices,
  getCurrencyDisplay
}: UnvalidatedSummaryProps) {
  const {
    i18n,
    t
  } = useTranslation();
  const {
    appData
  } = useContext(AppContext);
  const {
    service
  } = appData.bookingData;

  // Ignore lack of test coverage for locale "cimode" as it will only have "cimode" when in test mode
  /* istanbul ignore next */
  const locale = i18n.language !== "cimode" ? i18n.language : "en-GB";
  const multiPriceBookingTotal = bookingPrices.reduce<number>((acc, {
    price,
    quantity
  }) => {
    acc += price * quantity;
    return acc;
  }, 0);

  // Bookings with a single price and attendee
  if (bookingPrices.length === 1 && bookingPrices[0].quantity === 1) {
    if (service && service.bookingPaymentOption === PaymentOption.Deposit) {
      return <>
          <LineItem>
            <Label>{t("subtotal")}</Label>
            <Value>{getCurrencyDisplay(bookingPrices[0].price)}</Value>
          </LineItem>
          <LineItem>
            <Label>
              {(parseInt(service.bookingDepositAmount) / 100).toLocaleString(locale, {
              style: "percent"
            })}{" "}
              {t("deposit")}
            </Label>
            <Value>
              {getCurrencyDisplay(parseFloat(service.bookingDepositAmount) / 100 * bookingPrices[0].price)}
            </Value>
          </LineItem>
          <TotalLine {...{
          getCurrencyDisplay,
          total: parseFloat(service.bookingDepositAmount) / 100 * bookingPrices[0].price
        }} />
        </>;
    } else {
      return <TotalLine {...{
        getCurrencyDisplay,
        total: bookingPrices[0].price
      }} />;
    }
  }

  // Bookings with a single price and multiple attendees. This includes:
  //  * Standard bookings with more than one attendee,
  //  * Recurring bookings where all bookings are the same price,
  //  * Multi-tier bookings with more than one attendee all at a single price
  if (bookingPrices.length === 1 && bookingPrices[0].quantity > 1) {
    const {
      label,
      price: unitPrice,
      quantity
    } = bookingPrices[0];
    return <>
        <QuantityLine {...{
        getCurrencyDisplay,
        isTotal: true,
        label,
        quantity,
        unitPrice
      }} />
        {service && service.bookingPaymentOption === PaymentOption.Deposit && <>
            <LineItem>
              <Label>
                {(parseInt(service.bookingDepositAmount) / 100).toLocaleString(locale, {
              style: "percent"
            })}{" "}
                {t("deposit")}
              </Label>
              <Value>
                {getCurrencyDisplay(parseFloat(service.bookingDepositAmount) / 100 * (unitPrice * quantity))}
              </Value>
            </LineItem>
            <TotalLine {...{
          getCurrencyDisplay,
          total: parseFloat(service.bookingDepositAmount) / 100 * (unitPrice * quantity)
        }} />
          </>}
      </>;
  }

  // Bookings with multiple prices. This includes:
  //  * Multi-tier bookings with more than one selected price point at different prices
  //  * Recurring bookings where there's at least two prices among the selected bookings
  return <>
      {bookingPrices.map(({
      label,
      price: unitPrice,
      quantity
    }) => <QuantityLine key={unitPrice} {...{
      getCurrencyDisplay,
      label,
      quantity,
      unitPrice
    }} />)}
      {service && service.bookingPaymentOption === PaymentOption.Deposit ? <>
          <LineItem>
            <Label>{t("subtotal")}</Label>
            <Value>{getCurrencyDisplay(multiPriceBookingTotal)}</Value>
          </LineItem>
          <LineItem>
            <Label>
              {(parseInt(service.bookingDepositAmount) / 100).toLocaleString(locale, {
            style: "percent"
          })}{" "}
              {t("deposit")}
            </Label>
            <Value>
              {getCurrencyDisplay(parseFloat(service.bookingDepositAmount) / 100 * multiPriceBookingTotal)}
            </Value>
          </LineItem>
          <TotalLine {...{
        getCurrencyDisplay,
        total: parseFloat(service.bookingDepositAmount) / 100 * multiPriceBookingTotal
      }} />
        </> : <TotalLine {...{
      getCurrencyDisplay,
      total: bookingPrices.reduce<number>((acc, {
        price,
        quantity
      }) => {
        acc += price * quantity;
        return acc;
      }, 0)
    }} />}
    </>;
}